import * as THREE from "three";

/** Draw repeated rigid characters together while retaining their individual animation hierarchies.
 * Actor roots are detached: their transforms are local to this group's parent, not a rendered scene.
 */
export class RigidCrowd {
  readonly group = new THREE.Group();
  private readonly batches: {
    mesh: THREE.InstancedMesh;
    sources: { actor: THREE.Object3D; part: THREE.Mesh }[];
  }[] = [];

  constructor(private readonly actors: readonly THREE.Object3D[]) {
    const groups = new Map<string, { actor: THREE.Object3D; part: THREE.Mesh }[]>();
    for (const actor of actors)
      actor.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        if (object instanceof THREE.SkinnedMesh || Object.keys(object.geometry.morphAttributes).length)
          throw new Error("Rigid crowds require rigid animated parts");
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        const key = [
          object.geometry.uuid,
          ...materials.map((m) => m.uuid),
          object.castShadow,
          object.receiveShadow,
          object.renderOrder,
        ].join(":");
        const sources = groups.get(key) ?? [];
        sources.push({ actor, part: object });
        groups.set(key, sources);
      });
    for (const sources of groups.values()) {
      const source = sources[0]!.part;
      const mesh = new THREE.InstancedMesh(source.geometry, source.material, sources.length);
      mesh.name = `crew.${source.name}`;
      mesh.castShadow = source.castShadow;
      mesh.receiveShadow = source.receiveShadow;
      mesh.renderOrder = source.renderOrder;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(mesh);
      this.batches.push({ mesh, sources });
    }
    this.sync();
  }

  sync(): void {
    for (const actor of this.actors) actor.updateMatrixWorld(true);
    for (const { mesh, sources } of this.batches) {
      let count = 0;
      for (const { actor, part } of sources) {
        if (!actor.visible) continue;
        let visible = true;
        for (let node: THREE.Object3D | null = part; node && node !== actor; node = node.parent)
          if (!node.visible) visible = false;
        if (visible) mesh.setMatrixAt(count++, part.matrixWorld);
      }
      mesh.count = count;
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
  }

  dispose(): void {
    // Geometry/materials belong to the shared model cache; only instance buffers are ours.
    for (const { mesh } of this.batches) mesh.dispose();
    this.group.clear();
  }
}
